// ProvisionSshSandboxModal — "create SSH sandbox" = grant a developer (or an
// agent) SSH access to a braced sandbox on a worker by assigning their PUBLIC key.
// The principal is derived server-side as key:SHA256:<fp>; the key is a bearer
// credential, so revoke removes both the allow-list line and authorized_keys.
//
// SSO-domain members get a sandbox automatically (no pre-authorization needed), so
// this modal is only for the explicit key-grant case. Writing a trust line is the
// highest-consequence fleet action, so a review step precedes it; on success the
// derived fingerprint + principal are shown for the connect handoff.
//
// This modal only ADDS access — reviewing or revoking existing grants lives in
// ManageSshAccessModal (2026-09-10 split). They used to be one modal, with the
// grants list trailing below this form's own Cancel/Review buttons; an admin
// opening this modal specifically to revoke someone had to scroll past an
// unrelated add-flow to find it. Two different jobs, two modals now.
//
// Host comes FIRST (David + Curt, 2026-09-09 design review) — every sandbox lives
// on exactly one host, and until 2.7 a host enforces exactly one policy tier, so
// "which host" is the real decision and "which tier" follows from it automatically.
// The host list (with live free-capacity counts) comes from FleetPage's own already-
// loaded data via the `hosts` prop — no second fetch, and it's the same numbers the
// host cards show.
import { useCallback, useEffect, useState } from "react";
import { Modal, Form, Segmented, Input, Button, Alert, message, Tooltip } from "antd";
import { KeyRound, ShieldCheck, Copy, Check } from "lucide-react";
import { fleetApi, type GrantResult } from "@/lib/api/fleet";
import { PolicyChip } from "./PolicyChip";
import { HintIcon } from "@/components/agate";

const MONO = "var(--font-mono, 'JetBrains Mono Variable', monospace)";
type OwnerKind = "human" | "agent";

// What the modal needs per selectable host — FleetPage derives this from the
// same Host[]/byHost() it already renders the cards from.
export interface ProvisionHost {
  id: string;
  name: string;
  ip?: string;
  policyTier?: string;
  capacity: number;
  used: number;
}

const HELP = "Grant a developer or an agent SSH access to a braced sandbox on a fleet " +
  "worker. Pick the host, assign their SSH public key, and they connect with the " +
  "matching private key. It's a bearer credential — revoke here removes it. (People " +
  "in the SSO domain already get a sandbox automatically; this is for the explicit " +
  "key-grant case, e.g. an agent or an out-of-domain collaborator.)";

function FieldLabel({ label, hint }: { label: string; hint: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      {label}
      <HintIcon hint={hint} maxWidth={280} />
    </span>
  );
}

function Copyable({ text }: { text: string }) {
  const [done, setDone] = useState(false);
  return (
    <Tooltip title={done ? "Copied" : "Copy"}>
      <button aria-label="Copy" onClick={() => { navigator.clipboard?.writeText(text).then(() => { setDone(true); setTimeout(() => setDone(false), 1200); }); }}
        className="inline-flex flex-none items-center rounded p-1 text-text-muted transition-colors hover:text-accent">
        {done ? <Check size={13} /> : <Copy size={13} />}
      </button>
    </Tooltip>
  );
}

export default function ProvisionSshSandboxModal({ open, onClose, onChanged, hosts, lockedHostId }: {
  open: boolean;
  onClose: () => void;
  onChanged?: () => void;
  hosts: ProvisionHost[];
  // Set when opened from a specific host's own menu — the Host field
  // renders as fixed text instead of a Select (David + Curt, 2026-09-09:
  // "already hard-coded into the form"). Unset for the page-level CTA,
  // where the admin picks the host as the form's first step.
  lockedHostId?: string;
}) {
  const [hostId, setHostId] = useState<string>("");
  const [node, setNode] = useState<string>("");       // fleetd's IP for the selected host
  const [nodeName, setNodeName] = useState<string>("");
  const [tiers, setTiers] = useState<string[]>([]);   // live/authoritative from fleetd; tiers[0] is THE tier
  const [ownerKind, setOwnerKind] = useState<OwnerKind>("human");
  const [owner, setOwner] = useState("");
  const [assocHuman, setAssocHuman] = useState("");   // agent only: its human operator (optional)
  const [pubkey, setPubkey] = useState("");
  const [review, setReview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<GrantResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const selectedHost = hosts.find((h) => h.id === hostId);
  // Cached policyTier (from the Fleet page's own list) shows instantly; the
  // live fetch below overwrites it with fleetd's authoritative answer once
  // it lands — same tier in practice, but fleetd is the source of truth.
  const tier = tiers[0] ?? selectedHost?.policyTier;
  // Show the host by NAME (the Hosts-list identity) with the IP as context;
  // fall back to the IP alone when fleetd doesn't report a name (older fleetd).
  const nodeLabel = nodeName ? `${nodeName} (${node})` : (node || "—");

  const load = useCallback((ip: string | undefined) => {
    if (!ip) { setNode(""); setNodeName(""); setTiers([]); return; }
    fleetApi.access.list(ip).then((r) => {
      setNode(r.node); setNodeName(r.node_name || ""); setTiers(r.tiers || []);
    }).catch((e) => setErr(e instanceof Error ? e.message : "Couldn't load access"));
  }, []);

  useEffect(() => {
    if (!open) return;
    setOwnerKind("human"); setOwner(""); setAssocHuman(""); setPubkey("");
    setReview(false); setResult(null); setErr(null);
    const first = lockedHostId ? hosts.find((h) => h.id === lockedHostId) : hosts[0];
    setHostId(first?.id ?? "");
    load(first?.ip);
    // Re-running this on `hosts` identity changes (a poll tick on FleetPage)
    // would reset the admin's in-progress host pick — deliberately only `open`
    // (and `lockedHostId`, which only ever changes together with `open`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lockedHostId]);

  const canReview = !!hostId && !!node && !!tier && owner.trim().length > 0 && pubkey.trim().length > 0;

  const deploy = async () => {
    setBusy(true); setErr(null);
    try {
      const res = await fleetApi.access.grant({
        mode: "ssh-key", tier: tier!, node, owner: owner.trim(), owner_kind: ownerKind,
        associated_human: ownerKind === "agent" ? (assocHuman.trim() || undefined) : undefined,
        pubkey: pubkey.trim(),
      });
      setResult(res);
      message.success("Access granted");
      onChanged?.();
      load(selectedHost?.ip);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Grant failed");
      setReview(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onCancel={onClose} width={640} footer={null}
      title={
        <span className="inline-flex items-center gap-2">
          <KeyRound size={17} /> Create SSH Sandbox
          <HintIcon hint={HELP} maxWidth={360} />
        </span>
      }
      destroyOnHidden>
      {err && <Alert type="error" showIcon className="mb-3" message={err} />}

      {selectedHost && (
        <div className="mb-3 grid grid-cols-3 gap-4 border-b border-border pb-3">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">Host</div>
            <div className="text-[13px] text-text-primary" style={{ fontFamily: MONO }}>{selectedHost.name}</div>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">Policy</div>
            <div className="mt-0.5">
              {tier ? <PolicyChip tier={tier} linkable={false} /> : <span className="text-[13px] text-text-muted">—</span>}
            </div>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">Slots remaining</div>
            <div className="text-[13px] text-text-primary">
              {Math.max(selectedHost.capacity - selectedHost.used, 0)} of {selectedHost.capacity} free
            </div>
          </div>
        </div>
      )}

      {/* success handoff */}
      {result ? (
        <div>
          <Alert type="success" showIcon className="mb-3"
            message="Access granted" description={`On ${result.node_name || selectedHost?.name || result.node} · policy ${result.tier}`} />
          <div className="rounded-xl border border-border bg-surface-raised p-3">
            {result.principal && (
              <div className="mb-1 flex items-center justify-between gap-2">
                <span className="text-xs text-text-secondary">Principal</span>
                <span className="min-w-0 flex items-center gap-1 break-all text-[12.5px] text-text-primary" style={{ fontFamily: MONO }}>
                  {result.principal}<Copyable text={result.principal} /></span>
              </div>
            )}
            {result.fingerprint && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-text-secondary">Key fingerprint</span>
                <span className="break-all text-[12.5px] text-text-primary" style={{ fontFamily: MONO }}>{result.fingerprint}</span>
              </div>
            )}
          </div>
          <p className="mt-3 text-xs text-text-muted">
            The developer or agent can now SSH in with the matching private key. Use the host's Connect button for the exact address.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button onClick={() => setResult(null)}>Grant another</Button>
            <Button type="primary" onClick={onClose}>Done</Button>
          </div>
        </div>
      ) : review ? (
        /* review step */
        <div>
          <p className="mb-3 text-sm text-text-secondary">Review before granting — this writes a trust line on the worker.</p>
          <div className="rounded-xl border border-border bg-surface-elev px-4">
            <Row label="Node" value={nodeLabel} mono />
            <Row label="Owner" value={`${owner.trim()} (${ownerKind})`} mono />
            {ownerKind === "agent" && assocHuman.trim() && <Row label="Associated human" value={assocHuman.trim()} mono />}
            <Row label="Policy" value={tier || "—"} mono />
            <Row label="Public key" value={`…${pubkey.trim().slice(-32)}`} mono />
          </div>
          <p className="mt-3 text-xs text-text-muted">
            An ssh-key grant is a bearer credential — the binding is the trust line alone. Revoke removes both the allow-list and the key.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button onClick={() => setReview(false)} disabled={busy}>Back</Button>
            <Button type="primary" danger loading={busy} onClick={deploy}>Grant Access</Button>
          </div>
        </div>
      ) : (
        /* form step */
        <div>
          <Form layout="vertical">
            <Form.Item label={<FieldLabel label="Owner type" hint="Who holds this key — a person or an automated agent. Both get a braced sandbox under the chosen tier." />}>
              <Segmented value={ownerKind} onChange={(v) => setOwnerKind(v as OwnerKind)}
                options={[{ label: "Human", value: "human" }, { label: "Agent", value: "agent" }]} />
            </Form.Item>
            <Form.Item label={<FieldLabel label="Owner" hint="A label for this grant — never used as an auth identity." />} required>
              <Input value={owner} onChange={(e) => setOwner(e.target.value)}
                placeholder={ownerKind === "agent" ? "ci-bot / claude-agent" : "Jane Dev / eng-team"} />
            </Form.Item>
            {ownerKind === "agent" && (
              <Form.Item label={<FieldLabel label="Associated human" hint="Optional — the person who operates this agent, for accountability on the Users page." />}>
                <Input value={assocHuman} onChange={(e) => setAssocHuman(e.target.value)} placeholder="david@bluerocksecurity.net (optional)" />
              </Form.Item>
            )}
            <Form.Item label="SSH public key" required>
              <Input.TextArea value={pubkey} onChange={(e) => setPubkey(e.target.value)} rows={3}
                placeholder={ownerKind === "agent" ? "ssh-ed25519 AAAA… ci-bot" : "ssh-ed25519 AAAA… dev@laptop"}
                style={{ fontFamily: MONO, fontSize: 12 }} />
            </Form.Item>
          </Form>
          <div className="flex justify-end gap-2">
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" icon={<ShieldCheck size={15} />} disabled={!canReview} onClick={() => setReview(true)}>Review</Button>
          </div>
        </div>
      )}
    </Modal>
  );

  function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
    return (
      <div className="flex items-center justify-between gap-4 border-b border-border py-2 last:border-b-0">
        <span className="text-xs text-text-muted">{label}</span>
        <span className={`min-w-0 break-all text-right text-[13px] text-text-primary ${mono ? "" : "font-medium"}`}
          style={mono ? { fontFamily: MONO } : undefined}>{value}</span>
      </div>
    );
  }
}
